export const NODE_COPY_KEYS = [
  "kaimDev",
  "goals",
  "build",
  "photos",
  "breakglass",
  "home",
  "okolica",
  "michalowice",
  "houseGoal",
  "house",
  "marketEngine",
  "portfolioFrontier",
  "photoArchive",
  "aferyLekarskie",
] as const;

export const ORBITAL_COPY_KEYS = [
  "goalsDo",
  "goalsD1",
  "goalsFuturesUi",
  "goalsLedger",
  "kaimWorker",
  "kaimStudioApi",
  "kaimAccess",
  "kaimDesignSystem",
  "kaimNexusConnector",
  "buildBrief",
  "buildDocuments",
  "buildPlotIntel",
  "buildTimeline",
  "photosArchiveIndex",
  "photosObjectStore",
  "photosRozaInstance",
  "homeAssistant",
  "homePhysicalHost",
  "homeRemoteAccess",
  "okolicaCadastrePlanning",
  "okolicaTerrainEnvironment",
  "okolicaMobility",
  "okolicaEvidenceExport",
  "breakglassEdgeRuntime",
  "breakglassScenarioEngine",
] as const;

export const EVENT_TYPES = [
  "build.brief.updated",
  "goal.progress.updated",
  "portfolio.deployed",
  "photos.indexed",
  "home.simulation",
  "market.integrity",
  "build.state.sampled",
  "goals.market.sampled",
  "portfolio.edge.sampled",
  "photos.manifest.sampled",
  "home.awaiting-host",
  "location.context.sampled",
] as const;

export type NodeCopyKey = (typeof NODE_COPY_KEYS)[number];
export type OrbitalCopyKey = (typeof ORBITAL_COPY_KEYS)[number];
export type NexusEventType = (typeof EVENT_TYPES)[number];

export type NodeKind = "system" | "entity" | "artifact" | "location" | "goal";
export type NodeStatus = "online" | "offline" | "active" | "building" | "draft" | "warning";
export type NodeCluster = "core" | "projects" | "memory" | "infrastructure" | "life";
export type OrbitalKind = "service" | "runtime" | "storage" | "integration" | "artifact" | "library";
export type NexusRelation = "depends_on" | "presents" | "tracks" | "contains" | "relates_to" | "drives" | "located_at";
export type NexusEventSeverity = "info" | "success" | "warning";
export type CelestialArchetype = "stellar" | "black-hole";
export type OrbitalTone = "core" | "infra" | "memory" | "compute" | "signal";
export type CartographyLabelTier = "hero" | "major" | "minor";
export type CartographyLabelPlacement =
  "south" | "north" | "east" | "west" | "south-east" | "south-west" | "north-east" | "north-west";

export type NodeMetrics = {
  progress?: number;
  count?: number;
  version?: string;
  updatedAt?: string;
  meta?: string;
};

export type MeshNode = {
  id: string;
  copyKey: NodeCopyKey;
  kind: NodeKind;
  status: NodeStatus;
  cluster: NodeCluster;
  href?: string;
  metrics?: NodeMetrics;
};

export type OrbitalEntity = {
  id: string;
  parentId: string;
  copyKey: OrbitalCopyKey;
  kind: OrbitalKind;
  status: NodeStatus;
  cluster: NodeCluster;
  metrics?: NodeMetrics;
};

export type NexusEdge = {
  id: string;
  source: string;
  target: string;
  relation: NexusRelation;
  strength: number;
};

export type EventPayloadValue = string | number | boolean;
export type NexusEvent = {
  id: string;
  type: NexusEventType;
  sourceNodeId: string;
  targetNodeId?: string;
  timestamp: string;
  severity: NexusEventSeverity;
  payload?: Record<string, EventPayloadValue>;
};

export type NodeSpatialProjection = {
  position: [number, number, number];
  magnitude: number;
  archetype: CelestialArchetype;
  cartography: {
    labelTier: CartographyLabelTier;
    labelPlacement: CartographyLabelPlacement;
    labelOffsetX?: number;
    labelOffsetY?: number;
  };
};

export type EdgeSpatialProjection = {
  bend: number;
  skew: number;
  lift: number;
  thickness: number;
  opacityScale: number;
};

export type OrbitalSpatialProjection = {
  orbit: {
    radius: number;
    speed: number;
    tiltX: number;
    tiltZ: number;
    eccentricity: number;
    phase: number;
    ringOpacity?: number;
  };
  visual: {
    scale: number;
    ringCount: number;
    glow: number;
    tone: OrbitalTone;
  };
};

export type SpatialProjection = {
  nodes: Record<string, NodeSpatialProjection>;
  edges: Record<string, EdgeSpatialProjection>;
  orbitals: Record<string, OrbitalSpatialProjection>;
};

export type MeshSnapshot = {
  schemaVersion: 2;
  generatedAt: string;
  revision: string;
  nodes: MeshNode[];
  orbitals: OrbitalEntity[];
  edges: NexusEdge[];
  events: NexusEvent[];
  projection: SpatialProjection;
};
